import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.35);
  z-index: 9999;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 16px;
`;

const Banner = styled.div`
  width: min(980px, 100%);
  background: #0b3d73;
  color: white;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 18px 18px 16px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.25);
  font-size: 14px;
  font-family: 'Poppins', sans-serif;
`;

const TopRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Message = styled.p`
  margin: 0;
  line-height: 1.55;
  max-width: 740px;
`;

const CookieLink = styled(Link)`
  color: #93c5fd;
  text-decoration: underline;

  &:hover {
    color: #dbeafe;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Section = styled.div`
  margin-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  padding-top: 12px;
`;

const Button = styled.button`
  background-color: #dbeafe;
  color: #0b3d73;
  border: none;
  padding: 8px 16px;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  min-width: 120px;

  &:hover {
    background-color: #bfdbfe;
  }
`;

const DenyButton = styled(Button)`
  background-color: transparent;
  color: #e2e8f0;
  border: 1px solid rgba(226, 232, 240, 0.5);

  &:hover {
    background-color: rgba(226, 232, 240, 0.12);
  }
`;

const ManageButton = styled(Button)`
  background-color: transparent;
  color: #e2e8f0;
  border: 1px solid rgba(226, 232, 240, 0.5);

  &:hover {
    background-color: rgba(226, 232, 240, 0.12);
  }
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(200px, 1fr));
  gap: 10px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryCard = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 10px;
  cursor: pointer;
`;

const CategoryTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

const CategoryText = styled.div`
  font-size: 12px;
  color: #dbeafe;
  margin-top: 2px;
`;

const Switch = styled.input`
  margin-top: 2px;
`;

const FooterRow = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const SmallText = styled.p`
  margin: 0;
  font-size: 12px;
  color: #cbd5e1;
`;

const CONSENT_KEY = 'cookieConsentV2';

const DEFAULT_PREFERENCES = {
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
};

const parseSavedConsent = () => {
  const raw = localStorage.getItem(CONSENT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_PREFERENCES,
      ...parsed,
      necessary: true,
    };
  } catch (err) {
    return null;
  }
};

const saveConsent = (prefs) => {
  const payload = {
    version: 2,
    necessary: true,
    preferences: Boolean(prefs.preferences),
    analytics: Boolean(prefs.analytics),
    marketing: Boolean(prefs.marketing),
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
  localStorage.setItem(
    'cookieConsent',
    payload.preferences || payload.analytics || payload.marketing ? 'true' : 'false'
  );

  window.dispatchEvent(new CustomEvent('cookie-consent-updated', { detail: payload }));
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);

  useEffect(() => {
    const saved = parseSavedConsent();
    if (!saved) {
      setVisible(true);
      return;
    }
    setPreferences(saved);

    const openSettings = () => {
      setPreferences(parseSavedConsent() || DEFAULT_PREFERENCES);
      setShowPreferences(true);
      setVisible(true);
    };

    window.addEventListener('open-cookie-settings', openSettings);
    return () => window.removeEventListener('open-cookie-settings', openSettings);
  }, []);

  const handleAcceptAll = () => {
    const next = { ...DEFAULT_PREFERENCES, preferences: true, analytics: true, marketing: true };
    setPreferences(next);
    saveConsent(next);
    setVisible(false);
    setShowPreferences(false);
  };

  const handleRejectNonEssential = () => {
    const next = { ...DEFAULT_PREFERENCES };
    setPreferences(next);
    saveConsent(next);
    setVisible(false);
    setShowPreferences(false);
  };

  const handleSavePreferences = () => {
    const next = { ...preferences, necessary: true };
    setPreferences(next);
    saveConsent(next);
    setVisible(false);
    setShowPreferences(false);
  };

  const toggleCategory = (key) => {
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!visible) return null;

  return (
    <Overlay>
      <Banner role="dialog" aria-label="Cookie preferences">
        <TopRow>
          <Message>
            We use cookies to run the site and improve services. You can accept all, reject non-essential cookies, or
            choose categories. Read our <CookieLink to="/cookie">Cookie policy</CookieLink>.
          </Message>
          <ButtonRow>
            <Button onClick={handleAcceptAll}>Accept all</Button>
            <DenyButton onClick={handleRejectNonEssential}>Reject non-essential</DenyButton>
            <ManageButton onClick={() => setShowPreferences((v) => !v)}>
              {showPreferences ? 'Hide settings' : 'Manage settings'}
            </ManageButton>
          </ButtonRow>
        </TopRow>

        {showPreferences && (
          <Section>
            <CategoryGrid>
              <CategoryCard>
                <Switch type="checkbox" checked readOnly />
                <div>
                  <CategoryTitle>Strictly necessary</CategoryTitle>
                  <CategoryText>Required for security, login and core site functionality.</CategoryText>
                </div>
              </CategoryCard>

              <CategoryCard>
                <Switch
                  type="checkbox"
                  checked={preferences.preferences}
                  onChange={() => toggleCategory('preferences')}
                />
                <div>
                  <CategoryTitle>Preferences</CategoryTitle>
                  <CategoryText>Remember UI choices and improve convenience.</CategoryText>
                </div>
              </CategoryCard>

              <CategoryCard>
                <Switch type="checkbox" checked={preferences.analytics} onChange={() => toggleCategory('analytics')} />
                <div>
                  <CategoryTitle>Analytics</CategoryTitle>
                  <CategoryText>Measure usage to improve pages and performance.</CategoryText>
                </div>
              </CategoryCard>

              <CategoryCard>
                <Switch type="checkbox" checked={preferences.marketing} onChange={() => toggleCategory('marketing')} />
                <div>
                  <CategoryTitle>Marketing</CategoryTitle>
                  <CategoryText>Personalised ads and campaign attribution.</CategoryText>
                </div>
              </CategoryCard>
            </CategoryGrid>

            <FooterRow>
              <SmallText>You can update these settings any time from the footer.</SmallText>
              <Button onClick={handleSavePreferences}>Save preferences</Button>
            </FooterRow>
          </Section>
        )}
      </Banner>
    </Overlay>
  );
};

export default CookieBanner;
